import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { TriggerRule } from "@/types/theme";
import { useToast } from "@/components/ui/use-toast";

export const useTriggerRuleMutations = (id: string | undefined) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const addTriggerRule = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error("No chatbot ID provided");
      const { error } = await supabase
        .from("trigger_rules")
        .insert([{
          chatbot_id: id,
          name: "New Trigger",
          conditions: [],
          postvariablemappings: [], // Database column name
          active: true
        }]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trigger_rules", id] });
      toast({
        title: "Trigger rule added",
        description: "New trigger rule has been added successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  const updateTriggerRule = useMutation({
    mutationFn: async ({ ruleId, updates }: { ruleId: string; updates: Partial<TriggerRule> }) => {
      if (!id) throw new Error("No chatbot ID provided");
      
      // Create a DB-friendly object for the update
      const dbUpdates: Record<string, any> = {};
      
      // Process each field in the updates object
      if ('name' in updates) dbUpdates.name = updates.name;
      if ('notes' in updates) dbUpdates.notes = updates.notes;
      if ('active' in updates) dbUpdates.active = updates.active;
      if ('webhookUrl' in updates) dbUpdates.webhookurl = updates.webhookUrl;
      
      // Handle conditions separately (array of objects needs to be converted to JSON)
      if ('conditions' in updates && Array.isArray(updates.conditions)) {
        // Ensure it's stored as a JSON string if the DB column expects that,
        // or if we want to be absolutely sure about the format being sent.
        try {
          dbUpdates.conditions = JSON.stringify(updates.conditions);
        } catch (e) {
          console.error("Failed to stringify conditions:", e);
          // Decide on error handling: throw, or send original, or skip
          // For now, let's rethrow to make the problem visible
          throw new Error("Failed to stringify conditions for database update.");
        }
      }
      
      // Handle postVariableMappings to match the database column name
      if ('postVariableMappings' in updates && Array.isArray(updates.postVariableMappings)) {
        dbUpdates.postvariablemappings = updates.postVariableMappings;
      }
      
      const { error } = await supabase
        .from("trigger_rules")
        .update(dbUpdates)
        .eq("id", ruleId)
        .eq("chatbot_id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trigger_rules", id] });
      toast({
        title: "Trigger rule updated",
        description: "Trigger rule has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
      console.error("Error updating trigger rule:", error);
    },
  });

  const deleteTriggerRule = useMutation({
    mutationFn: async (ruleId: string) => {
      if (!id) throw new Error("No chatbot ID provided");
      const { error } = await supabase
        .from("trigger_rules")
        .delete()
        .eq("id", ruleId)
        .eq("chatbot_id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trigger_rules", id] });
      toast({
        title: "Trigger rule deleted",
        description: "Trigger rule has been deleted successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    },
  });

  return { addTriggerRule, updateTriggerRule, deleteTriggerRule };
};
